import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, forwardRef, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import {
  COMPUTED_OPERATIONS,
  ComputedOperation,
  FormComputedCase,
  FormComputedExpression,
  FormComputedObjectEntry,
  FormComputedOperand,
  FormComputedThreshold
} from '../../../shared/dynamic-form/form-schema.models';

interface SelectOption<T = string> {
  label: string;
  value: T;
}

@Component({
  standalone: true,
  selector: 'ain-form-builder-computed-expression-editor',
  imports: [
    NgTemplateOutlet,
    FormsModule,
    ButtonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    AinFormFieldComponent,
    forwardRef(() => FormBuilderComputedExpressionEditorComponent)
  ],
  template: `
    <section class="expr" [class.nested]="depth() > 0">
      <ain-form-field [label]="t('dynamic.builder.computed.operation')">
        <p-select
          [options]="operationOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="expression().operation"
          [disabled]="!canEdit()"
          appendTo="body"
          (ngModelChange)="changeOperation($event)" />
      </ain-form-field>

      @if (expression().operation === 'if') {
        <div class="branch">
          <strong>{{ t('dynamic.builder.computed.ifCondition') }}</strong>
          @for (operand of expression().operands; track $index; let idx = $index) {
            <ng-container *ngTemplateOutlet="operandEditor; context: { $implicit: operand, index: idx }" />
          }
        </div>
        <div class="branch">
          <strong>{{ t('dynamic.builder.computed.thenBranch') }}</strong>
          @if (expression().then; as thenExpr) {
            <ain-form-builder-computed-expression-editor
              [expression]="thenExpr"
              [fieldOptions]="fieldOptions()"
              [canEdit]="canEdit()"
              [depth]="depth() + 1"
              (expressionChange)="patch({ then: $event })" />
          }
        </div>
        <div class="branch">
          <strong>{{ t('dynamic.builder.computed.elseBranch') }}</strong>
          @if (expression().else; as elseExpr) {
            <ain-form-builder-computed-expression-editor
              [expression]="elseExpr"
              [fieldOptions]="fieldOptions()"
              [canEdit]="canEdit()"
              [depth]="depth() + 1"
              (expressionChange)="patch({ else: $event })" />
          }
        </div>
      } @else if (expression().operation === 'map') {
        <div class="operands-header">
          <strong>{{ t('dynamic.builder.computed.mapSource') }}</strong>
        </div>
        @for (operand of expression().operands; track $index; let idx = $index) {
          <ng-container *ngTemplateOutlet="operandEditor; context: { $implicit: operand, index: idx }" />
        }
        <div class="operands-header">
          <strong>{{ t('dynamic.builder.computed.cases') }}</strong>
          @if (canEdit()) {
            <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.computed.addCase')" (click)="addCase()"></button>
          }
        </div>
        @for (mapCase of expression().cases ?? []; track $index; let idx = $index) {
          <article class="operand-card">
            <ain-form-field [label]="t('dynamic.builder.computed.caseWhen')">
              <input pInputText class="w-full" [ngModel]="mapCase.when ?? ''" [disabled]="!canEdit()" (ngModelChange)="updateCaseWhen(idx, $event)" />
            </ain-form-field>
            <ain-form-field [label]="t('dynamic.builder.computed.caseThen')">
              <p-inputNumber [ngModel]="asNumber(mapCase.thenValue)" [disabled]="!canEdit()" [useGrouping]="false" (ngModelChange)="updateCaseThen(idx, $event)" />
            </ain-form-field>
            @if (canEdit()) {
              <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeCase(idx)"></button>
            }
          </article>
        }
      } @else if (expression().operation === 'band') {
        <div class="operands-header">
          <strong>{{ t('dynamic.builder.computed.bandSource') }}</strong>
        </div>
        @for (operand of expression().operands; track $index; let idx = $index) {
          <ng-container *ngTemplateOutlet="operandEditor; context: { $implicit: operand, index: idx }" />
        }
        <div class="operands-header">
          <strong>{{ t('dynamic.builder.computed.thresholds') }}</strong>
          @if (canEdit()) {
            <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.computed.addThreshold')" (click)="addThreshold()"></button>
          }
        </div>
        @for (threshold of expression().thresholds ?? []; track $index; let idx = $index) {
          <article class="operand-card threshold-grid">
            <ain-form-field [label]="t('dynamic.builder.computed.thresholdMin')">
              <p-inputNumber [ngModel]="threshold.min" [disabled]="!canEdit()" [useGrouping]="false" (ngModelChange)="updateThreshold(idx, { min: $event ?? 0 })" />
            </ain-form-field>
            <ain-form-field [label]="t('dynamic.builder.computed.thresholdMax')">
              <p-inputNumber [ngModel]="threshold.max ?? null" [disabled]="!canEdit()" [useGrouping]="false" (ngModelChange)="updateThreshold(idx, { max: $event ?? undefined })" />
            </ain-form-field>
            <ain-form-field [label]="t('dynamic.builder.computed.thresholdLabel')">
              <input pInputText class="w-full" [ngModel]="threshold.label" [disabled]="!canEdit()" (ngModelChange)="updateThreshold(idx, { label: $event })" />
            </ain-form-field>
            @if (canEdit()) {
              <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeThreshold(idx)"></button>
            }
          </article>
        }
      } @else if (expression().operation === 'object') {
        <div class="operands-header">
          <strong>{{ t('dynamic.builder.computed.entries') }}</strong>
          @if (canEdit()) {
            <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.computed.addEntry')" (click)="addEntry()"></button>
          }
        </div>
        @for (entry of expression().entries ?? []; track $index; let idx = $index) {
          <article class="operand-card">
            <ain-form-field [label]="t('dynamic.builder.computed.entryKey')">
              <input pInputText class="w-full" [ngModel]="entry.key" [disabled]="!canEdit()" (ngModelChange)="updateEntryKey(idx, $event)" />
            </ain-form-field>
            <ng-container *ngTemplateOutlet="operandEditor; context: { $implicit: entry.value ?? { kind: 'const', value: 0 }, index: idx, entryMode: true }" />
            @if (canEdit()) {
              <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeEntry(idx)"></button>
            }
          </article>
        }
      } @else if (expression().operation !== 'now') {
        <div class="operands-header">
          <strong>{{ t('dynamic.builder.computed.operands') }}</strong>
          @if (canEdit()) {
            <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.computed.addOperand')" (click)="addOperand()"></button>
          }
        </div>
        @for (operand of expression().operands; track $index; let idx = $index) {
          <ng-container *ngTemplateOutlet="operandEditor; context: { $implicit: operand, index: idx }" />
        }
      }
    </section>

    <ng-template #operandEditor let-operand let-index="index" let-entryMode="entryMode">
      <article class="operand-card">
        <ain-form-field [label]="t('dynamic.builder.computed.operandKind')">
          <p-select
            [options]="operandKindOptions()"
            optionLabel="label"
            optionValue="value"
            [ngModel]="operand.expression ? 'expression' : operand.kind"
            [disabled]="!canEdit()"
            appendTo="body"
            (ngModelChange)="updateOperandKind(index, $event, !!entryMode)" />
        </ain-form-field>

        @if (operand.expression) {
          <ain-form-builder-computed-expression-editor
            [expression]="operand.expression"
            [fieldOptions]="fieldOptions()"
            [canEdit]="canEdit()"
            [depth]="depth() + 1"
            (expressionChange)="updateOperandExpression(index, $event, !!entryMode)" />
        } @else if (operand.kind === 'field') {
          <ain-form-field [label]="t('dynamic.builder.computed.fieldReference')">
            <p-select
              [options]="fieldOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="operand.fieldKey ?? ''"
              [disabled]="!canEdit()"
              [filter]="true"
              appendTo="body"
              (ngModelChange)="updateOperandField(index, $event, !!entryMode)" />
          </ain-form-field>
        } @else {
          <ain-form-field [label]="t('dynamic.builder.computed.constantValue')">
            @if (usesTextConstant()) {
              <input pInputText class="w-full" [ngModel]="asText(operand.value)" [disabled]="!canEdit()" (ngModelChange)="updateOperandValue(index, $event, !!entryMode)" />
            } @else {
              <p-inputNumber [ngModel]="asNumber(operand.value)" [disabled]="!canEdit()" [useGrouping]="false" (ngModelChange)="updateOperandValue(index, $event ?? 0, !!entryMode)" />
            }
          </ain-form-field>
        }

        @if (canEdit() && !entryMode) {
          <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeOperand(index)"></button>
        }
      </article>
    </ng-template>
  `,
  styles: [`
    .expr { display: grid; gap: 10px; }
    .expr.nested {
      margin-inline-start: 8px;
      padding: 10px;
      border: 1px dashed #cbd5e1;
      border-radius: 8px;
      background: #f8fafc;
    }
    .operands-header, .branch { display: grid; gap: 8px; }
    .operands-header { grid-template-columns: 1fr auto; align-items: center; }
    .operand-card {
      display: grid;
      gap: 8px;
      padding: 10px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      background: #fff;
    }
    .threshold-grid { grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); }
  `]
})
export class FormBuilderComputedExpressionEditorComponent {
  private readonly localization = inject(LocalizationService);

  readonly expression = input.required<FormComputedExpression>();
  readonly fieldOptions = input.required<SelectOption[]>();
  readonly canEdit = input(true);
  readonly depth = input(0);

  readonly expressionChange = output<FormComputedExpression>();

  readonly operationOptions = computed<SelectOption<ComputedOperation>[]>(() =>
    COMPUTED_OPERATIONS.map(value => ({
      value,
      label: this.t(`dynamic.builder.computed.operation.${value}`)
    }))
  );

  readonly operandKindOptions = computed<SelectOption[]>(() => [
    { label: this.t('dynamic.builder.computed.kindField'), value: 'field' },
    { label: this.t('dynamic.builder.computed.kindConst'), value: 'const' },
    { label: this.t('dynamic.builder.computed.kindExpression'), value: 'expression' }
  ]);

  usesTextConstant(): boolean {
    return ['concat', 'upper', 'lower', 'coalesce'].includes(String(this.expression().operation).toLowerCase());
  }

  asNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  asText(value: unknown): string {
    return value == null ? '' : String(value);
  }

  changeOperation(operation: ComputedOperation): void {
    if (operation === 'if') {
      this.emit({
        operation: 'if',
        operands: [{ kind: 'field', fieldKey: this.fieldOptions()[0]?.value ?? '' }],
        then: { operation: 'sum', operands: [{ kind: 'const', value: 0 }] },
        else: { operation: 'sum', operands: [{ kind: 'const', value: 0 }] }
      });
      return;
    }
    if (operation === 'map') {
      this.emit({
        operation: 'map',
        operands: [{ kind: 'field', fieldKey: this.fieldOptions()[0]?.value ?? '' }],
        cases: [{ when: '', thenValue: 0 }]
      });
      return;
    }
    if (operation === 'band') {
      this.emit({
        operation: 'band',
        operands: [{ kind: 'field', fieldKey: this.fieldOptions()[0]?.value ?? '' }],
        thresholds: [{ min: 0, max: 100, label: 'low' }]
      });
      return;
    }
    if (operation === 'object') {
      this.emit({
        operation: 'object',
        operands: [],
        entries: [{ key: 'score', value: { kind: 'const', value: 0 } }]
      });
      return;
    }
    if (operation === 'now') {
      this.emit({ operation: 'now', operands: [] });
      return;
    }
    this.emit({
      operation,
      operands: this.expression().operands?.length
        ? this.expression().operands
        : [{ kind: 'const', value: 0 }]
    });
  }

  addOperand(): void {
    this.patch({ operands: [...(this.expression().operands ?? []), { kind: 'const', value: 0 }] });
  }

  removeOperand(index: number): void {
    this.patch({ operands: (this.expression().operands ?? []).filter((_, idx) => idx !== index) });
  }

  updateOperandKind(index: number, kind: string, entryMode: boolean): void {
    const operand: FormComputedOperand = kind === 'field'
      ? { kind: 'field', fieldKey: this.fieldOptions()[0]?.value ?? '' }
      : kind === 'expression'
        ? { kind: 'expression', expression: { operation: 'sum', operands: [{ kind: 'const', value: 0 }] } }
        : { kind: 'const', value: this.usesTextConstant() ? '' : 0 };
    if (entryMode) {
      this.updateEntryValue(index, operand);
      return;
    }
    const operands = [...(this.expression().operands ?? [])];
    operands[index] = operand;
    this.patch({ operands });
  }

  updateOperandField(index: number, fieldKey: string, entryMode: boolean): void {
    const operand: FormComputedOperand = { kind: 'field', fieldKey };
    if (entryMode) {
      this.updateEntryValue(index, operand);
      return;
    }
    const operands = [...(this.expression().operands ?? [])];
    operands[index] = operand;
    this.patch({ operands });
  }

  updateOperandValue(index: number, value: unknown, entryMode: boolean): void {
    const operand: FormComputedOperand = { kind: 'const', value };
    if (entryMode) {
      this.updateEntryValue(index, operand);
      return;
    }
    const operands = [...(this.expression().operands ?? [])];
    operands[index] = operand;
    this.patch({ operands });
  }

  updateOperandExpression(index: number, expression: FormComputedExpression, entryMode: boolean): void {
    const operand: FormComputedOperand = { kind: 'expression', expression };
    if (entryMode) {
      this.updateEntryValue(index, operand);
      return;
    }
    const operands = [...(this.expression().operands ?? [])];
    operands[index] = operand;
    this.patch({ operands });
  }

  addCase(): void {
    const cases = [...(this.expression().cases ?? []), { when: '', thenValue: 0 } satisfies FormComputedCase];
    this.patch({ cases });
  }

  removeCase(index: number): void {
    this.patch({ cases: (this.expression().cases ?? []).filter((_, idx) => idx !== index) });
  }

  updateCaseWhen(index: number, when: string): void {
    const cases = [...(this.expression().cases ?? [])];
    cases[index] = { ...cases[index], when };
    this.patch({ cases });
  }

  updateCaseThen(index: number, thenValue: number | null): void {
    const cases = [...(this.expression().cases ?? [])];
    cases[index] = { ...cases[index], thenValue: thenValue ?? 0, then: undefined };
    this.patch({ cases });
  }

  addThreshold(): void {
    const thresholds = [...(this.expression().thresholds ?? []), { min: 0, label: 'band' } satisfies FormComputedThreshold];
    this.patch({ thresholds });
  }

  removeThreshold(index: number): void {
    this.patch({ thresholds: (this.expression().thresholds ?? []).filter((_, idx) => idx !== index) });
  }

  updateThreshold(index: number, patchValue: Partial<FormComputedThreshold>): void {
    const thresholds = [...(this.expression().thresholds ?? [])];
    thresholds[index] = { ...thresholds[index], ...patchValue };
    this.patch({ thresholds });
  }

  addEntry(): void {
    const entries = [...(this.expression().entries ?? []), { key: 'key', value: { kind: 'const', value: 0 } } satisfies FormComputedObjectEntry];
    this.patch({ entries });
  }

  removeEntry(index: number): void {
    this.patch({ entries: (this.expression().entries ?? []).filter((_, idx) => idx !== index) });
  }

  updateEntryKey(index: number, key: string): void {
    const entries = [...(this.expression().entries ?? [])];
    entries[index] = { ...entries[index], key };
    this.patch({ entries });
  }

  updateEntryValue(index: number, value: FormComputedOperand): void {
    const entries = [...(this.expression().entries ?? [])];
    entries[index] = { ...entries[index], value };
    this.patch({ entries });
  }

  patch(partial: Partial<FormComputedExpression>): void {
    this.emit({ ...this.expression(), ...partial });
  }

  private emit(expression: FormComputedExpression): void {
    this.expressionChange.emit(expression);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
